import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';
import './Users.css';

const ALL_SCREENS = ['dashboard','customers','employees','clause_library','key_fields','msa','sow','engagements','invoices','timesheets','approvals','reports','user_management','settings'];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'Admin', label: 'Admin' },
  { value: 'HR Manager', label: 'HR Manager' },
  { value: 'User', label: 'User' },
];

const ACTIVE_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: '1', label: 'Active' },
  { value: '0', label: 'Inactive' },
];

function roleBadgeClass(role) {
  if (role === 'Admin') return 'badge-approved';
  if (role === 'HR Manager') return 'badge-submitted';
  return 'badge-pending';
}

function userInitials(fullName, username) {
  const s = (fullName || username || '?').trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function formatScreenLabel(screen) {
  return screen
    .split('_')
    .map(w => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

const WS_TAB_ICONS = {
  account: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  screens: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  customers: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="2" />
      <path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5" stroke="currentColor" strokeWidth="2" />
      <path d="M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', full_name: '', email: '', role: 'User', is_active: 1 });

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);

  const [screenAccess, setScreenAccess] = useState([]);
  const [accessIds, setAccessIds] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [custSearch, setCustSearch] = useState('');

  const [workspaceUser, setWorkspaceUser] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState('account');
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceNotice, setWorkspaceNotice] = useState('');

  const load = () => {
    setLoading(true);
    return api
      .get('/auth/users')
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/customers').then(setCustomers);
    api.get('/settings/customer-groups').then(setCustomerGroups);
  }, []);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  const filtered = useMemo(
    () =>
      users.filter(u => {
        if (roleFilter && u.role !== roleFilter) return false;
        if (activeFilter !== '') {
          const active = u.is_active ? '1' : '0';
          if (active !== activeFilter) return false;
        }
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return [String(u.id), u.username, u.full_name, u.email]
          .some(v => (v || '').toString().toLowerCase().includes(q));
      }),
    [users, search, roleFilter, activeFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, activeFilter, pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filtered.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [filtered.length, pageSize]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  const stats = useMemo(() => {
    const activeCount = users.filter(u => u.is_active).length;
    const adminCount = users.filter(u => u.role === 'Admin').length;
    return { activeCount, adminCount };
  }, [users]);

  const hasFilters = !!(search.trim() || roleFilter || activeFilter !== '');
  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setActiveFilter('');
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSaveNewUser = async () => {
    try {
      await api.post('/auth/users', form);
      setShowModal(false);
      setForm({ username: '', password: '', full_name: '', email: '', role: 'User', is_active: 1 });
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const flashNotice = msg => {
    setWorkspaceNotice(msg);
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setWorkspaceNotice(''), 3200);
  };

  const closeUserWorkspace = () => {
    setWorkspaceUser(null);
    setWorkspaceTab('account');
    setWorkspaceLoading(false);
    setWorkspaceNotice('');
    setScreenAccess([]);
    setAccessIds([]);
  };

  const openUserWorkspace = async u => {
    setWorkspaceUser(u);
    setWorkspaceTab('account');
    setWorkspaceNotice('');
    setForm({ username: u.username, password: '', full_name: u.full_name, email: u.email, role: u.role, is_active: u.is_active });
    setGroupFilter('');
    setCustSearch('');
    setWorkspaceLoading(true);
    try {
      const [access, custRows] = await Promise.all([
        api.get(`/auth/users/${u.id}/screen-access`),
        api.get(`/auth/users/${u.id}/customer-access`),
      ]);
      const mapped = ALL_SCREENS.map(screen => {
        const existing = access.find(a => a.screen === screen);
        return {
          screen,
          can_view: existing?.can_view || 0,
          can_create: existing?.can_create || 0,
          can_edit: existing?.can_edit || 0,
          can_delete: existing?.can_delete || 0,
        };
      });
      setScreenAccess(mapped);
      setAccessIds(custRows.map(a => a.customer_id));
    } catch (e) {
      alert(e.message || 'Could not load access settings.');
      closeUserWorkspace();
      return;
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const openNew = () => {
    setForm({ username: '', password: '', full_name: '', email: '', role: 'User', is_active: 1 });
    setShowModal(true);
  };

  const toggleScreen = (screen, field) => {
    setScreenAccess(prev => prev.map(s => (s.screen === screen ? { ...s, [field]: s[field] ? 0 : 1 } : s)));
  };

  const saveScreenAccess = async () => {
    if (!workspaceUser) return;
    try {
      await api.put(`/auth/users/${workspaceUser.id}/screen-access`, { screens: screenAccess });
      flashNotice('Screen access saved.');
    } catch (e) {
      alert(e.message);
    }
  };

  const saveAccess = async () => {
    if (!workspaceUser) return;
    try {
      await api.put(`/auth/users/${workspaceUser.id}/customer-access`, { customer_ids: accessIds });
      flashNotice('Customer data access saved.');
    } catch (e) {
      alert(e.message);
    }
  };

  const saveWorkspaceAccount = async () => {
    if (!workspaceUser) return;
    try {
      await api.put(`/auth/users/${workspaceUser.id}`, form);
      setWorkspaceUser(w => ({
        ...w,
        full_name: form.full_name,
        email: form.email,
        role: form.role,
        is_active: form.is_active,
      }));
      await load();
      flashNotice('Account details saved.');
    } catch (e) {
      alert(e.message);
    }
  };

  const toggleAccess = cid => setAccessIds(prev => (prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]));

  const filteredCustomers = customers.filter(c => {
    if (groupFilter && String(c.customer_group_id) !== groupFilter) return false;
    if (custSearch) {
      const s = custSearch.toLowerCase();
      return `${c.customer_id} ${c.legal_name}`.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <>
      <div className="page-header">
        <h1>User Management</h1>
      </div>
      <div className="page-content users-list-page clause-library-page">
        <div className="customers-top">
          <div className="customers-stats" aria-label="User counts">
            <div className="customers-stat">
              <div className="customers-stat-label">Total users</div>
              <div className="customers-stat-value">{loading ? '—' : users.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Match filters</div>
              <div className="customers-stat-value">{loading ? '—' : filtered.length.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Active</div>
              <div className="customers-stat-value">{loading ? '—' : stats.activeCount.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Admins</div>
              <div className="customers-stat-value">{loading ? '—' : stats.adminCount.toLocaleString()}</div>
            </div>
          </div>
          <div className="customers-top-right">
            {hasFilters && (
              <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
            <button type="button" className="btn btn-primary btn-sm customers-new-btn" onClick={openNew}>
              + New User
            </button>
          </div>
        </div>

        {users.length > 0 && filtered.length < users.length && (
          <div className="customers-filter-hint" role="status">
            <span>
              Filters narrow the table to <strong>{filtered.length}</strong> of <strong>{users.length}</strong> user
              {users.length === 1 ? '' : 's'}.
            </span>
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        <div className="customers-toolbar">
          <div className="customers-toolbar-left">
            <div className="customers-search">
              <span className="customers-search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
                  <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by ID, username, name, or email…"
                aria-label="Search users"
              />
            </div>
            <div className="customers-filters">
              <ToolbarFilterDropdown
                id="users-filter-role"
                labelText="Role"
                ariaLabel="Filter by role"
                value={roleFilter}
                onChange={setRoleFilter}
                placeholder="All roles"
                options={ROLE_OPTIONS}
                menuKey="users-role"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
              <ToolbarFilterDropdown
                id="users-filter-active"
                labelText="Account"
                ariaLabel="Filter by active status"
                value={activeFilter}
                onChange={setActiveFilter}
                placeholder="All statuses"
                options={ACTIVE_OPTIONS}
                menuKey="users-active"
                openMenu={filterMenuOpen}
                setOpenMenu={setFilterMenuOpen}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="users-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Full name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(u => (
                  <tr key={u.id}>
                    <td className="tabular-nums">{u.id}</td>
                    <td className="users-table-username" title={u.username}>{u.username}</td>
                    <td title={u.full_name}>{u.full_name}</td>
                    <td title={u.email}>
                      <span className="users-table-email">{u.email}</span>
                    </td>
                    <td>
                      <span className={`badge ${roleBadgeClass(u.role)}`}>{u.role}</span>
                    </td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-approved' : 'badge-rejected'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="users-table-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary users-edit-btn"
                          onClick={() => openUserWorkspace(u)}
                          title="Edit user, screen access, and customer data"
                          aria-label={`Edit ${u.username}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path
                              d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>Edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={7} className="users-table-empty">
                      {loading
                        ? 'Loading users…'
                        : users.length === 0
                          ? 'No users found.'
                          : 'No users match the filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalFiltered > 0 && (
            <div className="customers-pagination" role="navigation" aria-label="Table pagination">
              <div className="customers-pagination-summary">
                <span className="customers-pagination-summary-label">Showing</span>
                <span className="customers-pagination-summary-range">
                  <strong className="tabular-nums">{rangeStart}</strong>
                  <span className="customers-pagination-summary-dash">–</span>
                  <strong className="tabular-nums">{rangeEnd}</strong>
                  <span className="customers-pagination-summary-of">of</span>
                  <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
                  <span className="customers-pagination-summary-unit">rows</span>
                </span>
              </div>
              <div className="customers-pagination-controls">
                <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
                  <span className="customers-pagination-label" id="users-page-size-label">
                    Rows per page
                  </span>
                  <div className="customers-page-size-dd-inner">
                    <button
                      type="button"
                      className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
                      aria-haspopup="listbox"
                      aria-expanded={pageSizeOpen}
                      aria-label={`Rows per page, current value ${pageSize}`}
                      onClick={() => setPageSizeOpen(o => !o)}
                    >
                      <span>{pageSize}</span>
                      <span className="customers-page-size-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {pageSizeOpen && (
                      <ul className="customers-page-size-menu" role="listbox" aria-labelledby="users-page-size-label">
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <li
                            key={n}
                            role="option"
                            aria-selected={pageSize === n}
                            className={
                              pageSize === n
                                ? 'customers-page-size-option customers-page-size-option-active'
                                : 'customers-page-size-option'
                            }
                            onMouseDown={e => {
                              e.preventDefault();
                              setPageSize(n);
                              setPageSizeOpen(false);
                            }}
                          >
                            {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(1)}
                    aria-label="First page"
                    title="First page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 19l-7-7 7-7" />
                        <path d="M18 19l-7-7 7-7" />
                      </svg>
                    </span>
                    <span>First</span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </span>
                    <span>Prev</span>
                  </button>
                  <div className="customers-pagination-page-chip">
                    <span className="customers-pagination-page-label">Page</span>
                    <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
                    <span className="customers-pagination-page-sep">/</span>
                    <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span>Next</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(totalPages)}
                    aria-label="Last page"
                    title="Last page"
                  >
                    <span>Last</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 5l7 7-7 7" />
                        <path d="M6 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>New user</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Username</label>
                <input value={form.username} onChange={e => setField('username', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={form.password} onChange={e => setField('password', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Full Name</label>
                <input value={form.full_name} onChange={e => setField('full_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={form.role} onChange={e => setField('role', e.target.value)}>
                  <option>Admin</option>
                  <option>HR Manager</option>
                  <option>User</option>
                </select>
              </div>
              <div className="form-group">
                <label>Active</label>
                <select value={form.is_active} onChange={e => setField('is_active', parseInt(e.target.value, 10))}>
                  <option value={1}>Yes</option>
                  <option value={0}>No</option>
                </select>
              </div>
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={handleSaveNewUser}>
                Create user
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {workspaceUser && (
        <div className="modal-overlay users-workspace-overlay" onClick={closeUserWorkspace}>
          <div className="modal users-workspace-modal" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="users-workspace-title" aria-modal="true">
            <button type="button" className="users-workspace-close-btn" onClick={closeUserWorkspace} aria-label="Close editor">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <div className="users-workspace-head">
              <div className="users-workspace-avatar" aria-hidden>
                {userInitials(workspaceUser.full_name, workspaceUser.username)}
              </div>
              <div className="users-workspace-head-text">
                <p className="users-workspace-kicker">Edit user</p>
                <h2 id="users-workspace-title" className="users-workspace-title">
                  {workspaceUser.full_name || workspaceUser.username}
                </h2>
                <div className="users-workspace-meta">
                  <span className="users-workspace-username">@{workspaceUser.username}</span>
                  <span className={`badge ${roleBadgeClass(workspaceUser.role)}`}>{workspaceUser.role}</span>
                  <span className={`badge ${workspaceUser.is_active ? 'badge-approved' : 'badge-rejected'}`}>
                    {workspaceUser.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="users-workspace-intro">
              <p className="users-workspace-lead">
                Adjust account details, UI permissions, and which customer records apply. Changes apply when you save each section.
              </p>
              {workspaceNotice ? (
                <div className="users-workspace-notice" role="status">
                  <span className="users-workspace-notice-icon" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {workspaceNotice}
                </div>
              ) : null}
            </div>

            <div className="users-workspace-tabs-wrap">
              <div className="users-workspace-tabs" role="tablist" aria-label="Edit user sections">
                {[
                  { id: 'account', label: 'Account' },
                  { id: 'screens', label: 'Screen access' },
                  { id: 'customers', label: 'Customer data' },
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    id={`users-ws-tab-${t.id}`}
                    aria-controls={`users-ws-panel-${t.id}`}
                    aria-selected={workspaceTab === t.id}
                    className={`users-workspace-tab ${workspaceTab === t.id ? 'users-workspace-tab-active' : ''}`}
                    onClick={() => setWorkspaceTab(t.id)}
                  >
                    <span className="users-workspace-tab-icon">{WS_TAB_ICONS[t.id]}</span>
                    <span className="users-workspace-tab-label">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="users-workspace-panels">
              <div
                id="users-ws-panel-account"
                role="tabpanel"
                aria-labelledby="users-ws-tab-account"
                className="users-workspace-panel"
                hidden={workspaceTab !== 'account'}
              >
                <div className="users-workspace-panel-head">
                  <h3 className="users-workspace-panel-title">Account</h3>
                  <p className="users-workspace-panel-hint">
                    Username cannot be changed. Leave password empty to keep the current one.
                  </p>
                </div>
                <div className="users-workspace-card">
                  <h4 className="users-workspace-card-title">Sign-in</h4>
                  <div className="form-grid users-workspace-form">
                    <div className="form-group">
                      <label>Username</label>
                      <input value={form.username} disabled className="users-workspace-input-muted" />
                    </div>
                    <div className="form-group">
                      <label>New password</label>
                      <input
                        type="password"
                        value={form.password}
                        onChange={e => setField('password', e.target.value)}
                        placeholder="Leave blank to keep current"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </div>
                <div className="users-workspace-card">
                  <h4 className="users-workspace-card-title">Profile and role</h4>
                  <div className="form-grid users-workspace-form">
                    <div className="form-group">
                      <label>Full name</label>
                      <input value={form.full_name} onChange={e => setField('full_name', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Role</label>
                      <select value={form.role} onChange={e => setField('role', e.target.value)}>
                        <option>Admin</option>
                        <option>HR Manager</option>
                        <option>User</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Account active</label>
                      <select value={form.is_active} onChange={e => setField('is_active', parseInt(e.target.value, 10))}>
                        <option value={1}>Yes</option>
                        <option value={0}>No</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="users-workspace-actions">
                  <button type="button" className="btn btn-primary users-workspace-save" onClick={saveWorkspaceAccount}>
                    Save account
                  </button>
                </div>
              </div>

              <div
                id="users-ws-panel-screens"
                role="tabpanel"
                aria-labelledby="users-ws-tab-screens"
                className="users-workspace-panel"
                hidden={workspaceTab !== 'screens'}
              >
                <div className="users-workspace-panel-head">
                  <h3 className="users-workspace-panel-title">Screen access</h3>
                  <p className="users-workspace-panel-hint">
                    Set view, create, edit, and delete per screen. HR Managers always have full access to Employees.
                  </p>
                </div>
                {workspaceLoading ? (
                  <p className="users-workspace-loading">Loading permissions…</p>
                ) : (
                  <>
                    <div className="users-workspace-card users-workspace-card--flush">
                      <div className="table-container users-workspace-table-wrap">
                        <table className="users-screen-access-table users-screen-access-table--workspace">
                          <thead>
                            <tr>
                              <th>Screen</th>
                              <th>View</th>
                              <th>Create</th>
                              <th>Edit</th>
                              <th>Delete</th>
                            </tr>
                          </thead>
                          <tbody>
                            {screenAccess.map(s => (
                              <tr key={s.screen}>
                                <td className="users-screen-name">{formatScreenLabel(s.screen)}</td>
                                {['can_view', 'can_create', 'can_edit', 'can_delete'].map(field => (
                                  <td key={field} className="users-screen-check">
                                    <input
                                      type="checkbox"
                                      checked={!!s[field]}
                                      onChange={() => toggleScreen(s.screen, field)}
                                      aria-label={`${field.replace('can_', '').replace('_', ' ')} ${formatScreenLabel(s.screen)}`}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="users-workspace-actions">
                      <button type="button" className="btn btn-primary users-workspace-save" onClick={saveScreenAccess}>
                        Save screen access
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div
                id="users-ws-panel-customers"
                role="tabpanel"
                aria-labelledby="users-ws-tab-customers"
                className="users-workspace-panel"
                hidden={workspaceTab !== 'customers'}
              >
                <div className="users-workspace-panel-head">
                  <h3 className="users-workspace-panel-title">Customer data</h3>
                  <p className="users-workspace-panel-hint">
                    Non-admin users only see customers checked here (lists, MSA, SOW, billing, and related flows). With none
                    selected, they cannot open customer records.
                  </p>
                </div>
                {workspaceLoading ? (
                  <p className="users-workspace-loading">Loading customer access…</p>
                ) : (
                  <>
                    <div className="users-workspace-toolbar">
                      <div className="form-grid users-access-filters users-workspace-access-filters">
                        <div className="form-group">
                          <label>Filter by group</label>
                          <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}>
                            <option value="">All groups</option>
                            {customerGroups.map(g => (
                              <option key={g.id} value={g.id}>
                                {g.group_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group users-workspace-search-field">
                          <label>Search customers</label>
                          <div className="users-workspace-search-wrap">
                            <span className="users-workspace-search-icon" aria-hidden>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
                                <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </span>
                            <input
                              value={custSearch}
                              onChange={e => setCustSearch(e.target.value)}
                              placeholder="ID or legal name…"
                              aria-label="Search customers by ID or name"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="users-workspace-card users-workspace-card--flush">
                      <div className="users-access-list users-workspace-access-list">
                        {filteredCustomers.map(c => (
                          <label key={c.customer_id} className="users-access-row">
                            <input type="checkbox" checked={accessIds.includes(c.customer_id)} onChange={() => toggleAccess(c.customer_id)} />
                            <span className="users-access-row-id">{c.customer_id}</span>
                            <span className="users-access-row-name">{c.legal_name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="users-workspace-actions">
                      <button type="button" className="btn btn-primary users-workspace-save" onClick={saveAccess}>
                        Save customer access
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
